/**
 * A simple utility class that works with LabelManager to enable label positioning
 * functionality for labels on the map using a click-to-select, move, click-to-drop pattern.
 */
class SimpleLabelDragger {
  /**
   * Constructor for SimpleLabelDragger
   * @param {Object} config - Configuration object containing view, labelManager and callbacks
   */
  constructor(config) {
    // Handle both styles of initialization for backward compatibility
    if (arguments.length === 2 && arguments[0] && arguments[1]) {
      // Old style: separate parameters
      this.view = arguments[0];
      this.labelManager = arguments[1];
      this.onDragStart = null;
      this.onDrag = null;
      this.onDragEnd = null;
    } else if (config && typeof config === 'object') {
      // New style: config object
      this.view = config.view;
      this.labelManager = config.labelManager;
      this.onDragStart = config.onDragStart || null;
      this.onDrag = config.onDrag || null;
      this.onDragEnd = config.onDragEnd || null;
    } else {
      console.error("[SimpleLabelDragger] Invalid initialization parameters");
      this.view = null;
      this.labelManager = null;
    }

    // Event handlers
    this.handlers = [];
    this.domEventHandlers = [];
    
    // State tracking
    this.isActive = false;             // Whether the dragger is enabled
    this.selectedLabel = null;         // Currently selected label
    this.isMoving = false;             // Whether currently in moving state
    this.originalOffset = null;        // Original offset of the label 
    this.originalPosition = null;      // Original screen position
    this.currentPosition = null;       // Current mouse position
    this.originalNavState = null;      // Original map navigation state
    
    // Reference to the move indicator element
    this.moveIndicator = null;

    // Highlight graphics for visual feedback
    this.labelHighlight = null;
    this.dataPointHighlight = null;
    this.highlightLayer = null;

    // Hover highlight for visual feedback when hovering over labels
    this.hoverHighlight = null;
    this.hoveredLabel = null;
    this.hoverThrottleTimeout = null;
    this.lastHoverTime = 0;

    // Settings for move throttling
    this.lastMoveTime = 0;
    this.moveThrottle = 16; // milliseconds (roughly 60fps)

    // Track when we're waiting for a selection operation to complete
    this.isSelectionPending = false;

    // Direct drag mode state (click-and-hold to drag)
    this.isDirectDragging = false;
    this.directDragStartPoint = null;
    this.directDragOriginalGeometry = null;
    this.directDragHoldTimer = null;
    this.directDragThreshold = 150; // ms to hold before drag starts (reduced for responsiveness)
    this.directDragNavState = null; // Stores navigation state during direct drag
    this._pointerDownInProgress = false; // Guard against duplicate async pointer-down processing

    // CRITICAL: Track whether the Label Editor is open
    // Right-click dragging is ONLY allowed when this is true
    this.labelEditorOpen = false;

    // Canvas for text measurement
    this._canvasContext = null;

    // Cache for ArcGIS Point class to avoid repeated async imports during drag
    this._PointClass = null;

    // Initialize if we have both required references
    if (this.view && this.labelManager) {
      this.initialize();
    } else {
      console.warn("[SimpleLabelDragger] Missing required view or labelManager. Dragger inactive.");
    }

    // Add cleanup on page unload/refresh
    this._setupPageUnloadCleanup();
  }

  /**
   * Setup cleanup handler for page unload/refresh
   * @private
   */
  _setupPageUnloadCleanup() {
    if (typeof window !== 'undefined') {
      const cleanupHandler = () => {
        console.log("[SimpleLabelDragger] Page unloading - cleaning up highlights");
        this._removeHighlights();

        // Remove highlight layer if it exists
        if (this.highlightLayer && this.view?.map) {
          try {
            this.view.map.remove(this.highlightLayer);
          } catch (error) {
            // Silently ignore
          }
        }
      };

      window.addEventListener('beforeunload', cleanupHandler);
      window.addEventListener('pagehide', cleanupHandler);

      // Store reference for later cleanup
      this._pageUnloadHandler = cleanupHandler;
    }
  }

  /**
   * Create a valid screen point from an event or coordinates
   * @param {Object} event - The event object
   * @returns {Object|null} - A valid screen point object or null if invalid
   * @private
   */
  _createValidScreenPoint(event) {
    try {
      // If we already have a valid screen point, use it
      if (event.screenPoint && typeof event.screenPoint.x === 'number' && 
          typeof event.screenPoint.y === 'number') {
        return event.screenPoint;
      }
      
      // Try to create from x/y properties directly on the event
      if (event.x !== undefined && event.y !== undefined && 
          typeof event.x === 'number' && typeof event.y === 'number') {
        return { x: event.x, y: event.y };
      }
      
      // Try to extract from native event if available
      const nativeEvent = event.native || event;
      if (nativeEvent && 
          typeof nativeEvent.clientX === 'number' && 
          typeof nativeEvent.clientY === 'number') {
        
        // If we have a view container, adjust coordinates to be relative to it
        if (this.view && this.view.container) {
          const rect = this.view.container.getBoundingClientRect();
          return { 
            x: nativeEvent.clientX - rect.left, 
            y: nativeEvent.clientY - rect.top 
          };
        }
        
        // Otherwise use client coordinates directly
        return { x: nativeEvent.clientX, y: nativeEvent.clientY };
      }
      
      // If we're dealing with a DOM event
      if (event instanceof MouseEvent || event instanceof TouchEvent) {
        if (typeof event.clientX === 'number' && typeof event.clientY === 'number') {
          // If we have a view container, adjust coordinates
          if (this.view && this.view.container) {
            const rect = this.view.container.getBoundingClientRect();
            return { 
              x: event.clientX - rect.left, 
              y: event.clientY - rect.top 
            };
          }
          
          return { x: event.clientX, y: event.clientY };
        }
        
        // Handle touch events
        if (event.touches && event.touches.length > 0) {
          const touch = event.touches[0];
          if (typeof touch.clientX === 'number' && typeof touch.clientY === 'number') {
            if (this.view && this.view.container) {
              const rect = this.view.container.getBoundingClientRect();
              return { 
                x: touch.clientX - rect.left, 
                y: touch.clientY - rect.top 
              };
            }
            
            return { x: touch.clientX, y: touch.clientY };
          }
        }
      }
      
      // Fallback: return null for invalid screen point
      return null;
    } catch (error) {
      console.error("[SimpleLabelDragger] Error creating screen point:", error);
      return null;
    }
  }

  /**
   * Initialize the dragger with event handlers
   */
  async initialize() {
    console.log("[SimpleLabelDragger] Initializing label click handlers");

    // Clean up any existing handlers first
    this.cleanup();

    if (!this.view || !this.view.container) {
      console.error("[SimpleLabelDragger] Cannot initialize: Missing view or container");
      return;
    }

    // Create move indicator element
    this._createMoveIndicator();

    // Initialize highlight layer for yellow selection boxes
    await this._initializeHighlightLayer();

    // Set up direct drag handlers (click-and-hold drag for selected labels)
    this._setupDirectDragHandlers();
  }

  /**
   * Set up event handlers for direct drag functionality
   * These handlers allow dragging a selected label by clicking and holding on it
   * @private
   */
  _setupDirectDragHandlers() {
    console.log("[SimpleLabelDragger] Setting up direct drag handlers");

    // Pointer down handler - initiates drag when clicking on selected label
    const pointerDownHandler = this.view.on("pointer-down", (event) => {
      console.log("[SimpleLabelDragger] pointer-down event received, button:", event.button || event.native?.button);
      this._handlePointerDown(event);
    });
    this.handlers.push(pointerDownHandler);

    // Pointer move handler - handles drag movement
    const pointerMoveHandler = this.view.on("pointer-move", (event) => {
      this._handlePointerMove(event);
    });
    this.handlers.push(pointerMoveHandler);

    // Pointer up handler - ends drag
    const pointerUpHandler = this.view.on("pointer-up", (event) => {
      this._handlePointerUp(event);
    });
    this.handlers.push(pointerUpHandler);

    // DOM pointer up handler for reliable drag end detection
    // NOTE: We don't use a DOM pointer move handler because it uses different
    // coordinate system (clientX/Y) than the ArcGIS view events (screenPoint).
    // Using both causes coordinate mismatches and large delta jumps.
    const domPointerUpHandler = (event) => {
      if (this.isDirectDragging) {
        this._handlePointerUp(event);
      }
    };
    document.addEventListener('pointerup', domPointerUpHandler);
    this.domEventHandlers.push({
      element: document,
      type: 'pointerup',
      handler: domPointerUpHandler
    });

    // Escape key cancels drag
    const escapeHandler = (event) => {
      if (event.key === 'Escape' && this.isDirectDragging) {
        this._cancelDirectDrag();
      }
    };
    document.addEventListener('keydown', escapeHandler);
    this.domEventHandlers.push({
      element: document,
      type: 'keydown',
      handler: escapeHandler
    });

    console.log("[SimpleLabelDragger] Direct drag handlers set up successfully");
  }
  
  /**
   * Measure text width using canvas for accurate bounding boxes
   * @private
   * @param {string} text - The text to measure
   * @param {number} fontSize - Font size in pixels
   * @param {string} fontWeight - Font weight (normal, bold, etc.)
   * @param {string} fontFamily - Font family (e.g., "sans-serif", "Arial")
   * @returns {number} - Width in pixels
   */
  _measureTextWidth(text, fontSize, fontWeight = "normal", fontFamily = "sans-serif") {
    if (!this._canvasContext) {
      // Create a temporary canvas if not exists
      const canvas = document.createElement("canvas");
      this._canvasContext = canvas.getContext("2d");
    }

    // Set font properties using the actual font family from the label
    this._canvasContext.font = `${fontWeight} ${fontSize}px ${fontFamily}`;

    // Measure text
    const metrics = this._canvasContext.measureText(text);
    return metrics.width;
  }

  /**
   * Create the move indicator element that follows the cursor when moving
   * @private
   */
  _createMoveIndicator() {
    // No longer creating a visible indicator as requested
    this._removeMoveIndicator();
    this.moveIndicator = null;
  }
  
  /**
   * Remove the move indicator element
   * @private
   */
  _removeMoveIndicator() {
    if (this.moveIndicator && this.moveIndicator.parentNode) {
      this.moveIndicator.parentNode.removeChild(this.moveIndicator);
      this.moveIndicator = null;
    }
  }

  /**
   * Initialize or get the highlight layer for selection boxes
   * @private
   */
  async _initializeHighlightLayer() {
    try {
      // Check if highlight layer already exists
      const existingLayer = this.view.map.layers.find(
        layer => layer.id === 'label-edit-highlight-layer'
      );

      if (existingLayer) {
        this.highlightLayer = existingLayer;
        console.log("[SimpleLabelDragger] Using existing highlight layer");
        return;
      }

      // Dynamically import ArcGIS modules
      const [GraphicsLayer] = await Promise.all([
        import("@arcgis/core/layers/GraphicsLayer.js").then(m => m.default)
      ]);

      // Create new highlight layer - positioned on top of everything
      this.highlightLayer = new GraphicsLayer({
        id: 'label-edit-highlight-layer',
        title: 'Label Edit Highlights',
        listMode: 'hide',
        opacity: 1,
        visible: true,
        // Ensure it's on top
        elevationInfo: { mode: "on-the-ground" }
      });

      // Add layer at the absolute top of the map to ensure visibility
      // Get the current layer count and add at that index to place on top
      const topIndex = this.view.map.layers.length;
      this.view.map.add(this.highlightLayer, topIndex);

      console.log("[SimpleLabelDragger] Created highlight layer", {
        layerId: this.highlightLayer.id,
        visible: this.highlightLayer.visible,
        opacity: this.highlightLayer.opacity,
        layerIndex: this.view.map.layers.indexOf(this.highlightLayer),
        totalLayers: this.view.map.layers.length
      });
    } catch (error) {
      console.error("[SimpleLabelDragger] Error initializing highlight layer:", error);
    }
  }

  /**
   * Create a yellow highlight box around a graphic
   * @private
   * @param {Object} geometry - The geometry to highlight
   * @param {Object} labelGraphic - The label graphic (for text dimensions)
   * @param {number} padding - Padding around the geometry in pixels
   * @returns {Promise<Object|null>} - Highlight graphic or null
   */
  async _createHighlightBox(geometry, labelGraphic = null, padding = 10) {
    if (!geometry) {
      console.warn("[SimpleLabelDragger] No geometry provided to _createHighlightBox");
      return null;
    }

    try {
      const [Graphic, Polygon, SimpleFillSymbol] = await Promise.all([
        import("@arcgis/core/Graphic.js").then(m => m.default),
        import("@arcgis/core/geometry/Polygon.js").then(m => m.default),
        import("@arcgis/core/symbols/SimpleFillSymbol.js").then(m => m.default)
      ]);

      // Convert geometry to screen coordinates
      const screenPoint = this.view.toScreen(geometry);

      if (!screenPoint || screenPoint.x == null || screenPoint.y == null) {
        console.error("[SimpleLabelDragger] Invalid screen point from geometry");
        return null;
      }

      // Calculate text dimensions if this is a text label
      let boxWidth = padding * 2;
      let boxHeight = padding * 2;
      let alignmentOffsetX = 0;
      let alignmentOffsetY = 0;

      if (labelGraphic && labelGraphic.symbol && labelGraphic.symbol.type === "text") {
        const text = labelGraphic.symbol.text || "";
        const fontSize = labelGraphic.symbol.font?.size || 10;
        const fontWeight = labelGraphic.symbol.font?.weight || "normal";
        const fontFamily = labelGraphic.symbol.font?.family || "sans-serif";

        // Improved text dimension estimation using canvas measurement with actual font
        const textWidth = this._measureTextWidth(text, fontSize, fontWeight, fontFamily);
        const textHeight = fontSize * 1.4; // More accurate line height

        boxWidth = textWidth + (padding * 2);
        boxHeight = textHeight + (padding * 2);

        // Account for text alignment to ensure box starts at correct position
        const hAlign = labelGraphic.symbol.horizontalAlignment || "left";
        const vAlign = labelGraphic.symbol.verticalAlignment || "baseline";

        // Adjust box position based on alignment
        switch(hAlign) {
          case "left":
            alignmentOffsetX = boxWidth / 2; // Box extends to the right
            break;
          case "right":
            alignmentOffsetX = -boxWidth / 2; // Box extends to the left
            break;
          case "center":
          default:
            alignmentOffsetX = 0; // Box is centered
            break;
        }

        switch(vAlign) {
          case "top":
            alignmentOffsetY = boxHeight / 2; // Box extends downward
            break;
          case "bottom":
            alignmentOffsetY = -boxHeight / 2; // Box extends upward
            break;
          case "middle":
            alignmentOffsetY = 0; // Box is centered
            break;
          case "baseline":
          default:
            alignmentOffsetY = -boxHeight / 3; // Baseline is roughly 1/3 from bottom
            break;
        }
      }

      // Create box around the point with calculated dimensions and alignment
      const centerX = screenPoint.x + alignmentOffsetX;
      const centerY = screenPoint.y + alignmentOffsetY;

      const topLeft = this.view.toMap({
        x: centerX - (boxWidth / 2),
        y: centerY - (boxHeight / 2)
      });
      const topRight = this.view.toMap({
        x: centerX + (boxWidth / 2),
        y: centerY - (boxHeight / 2)
      });
      const bottomRight = this.view.toMap({
        x: centerX + (boxWidth / 2),
        y: centerY + (boxHeight / 2)
      });
      const bottomLeft = this.view.toMap({
        x: centerX - (boxWidth / 2),
        y: centerY + (boxHeight / 2)
      });

      // Verify we got valid map points
      if (!topLeft || !topRight || !bottomRight || !bottomLeft) {
        console.error("[SimpleLabelDragger] Failed to convert screen points to map points");
        return null;
      }

      // Create polygon from box coordinates
      // Use x/y properties (toMap returns Point with x/y, not longitude/latitude)
      const box = new Polygon({
        rings: [[
          [topLeft.x, topLeft.y],
          [topRight.x, topRight.y],
          [bottomRight.x, bottomRight.y],
          [bottomLeft.x, bottomLeft.y],
          [topLeft.x, topLeft.y]
        ]],
        spatialReference: this.view.spatialReference
      });

      // Create bright yellow outline symbol with 80% transparency
      const symbol = new SimpleFillSymbol({
        color: [255, 255, 0, 0.2], // 80% transparent yellow fill (0.2 = 20% opacity)
        outline: {
          color: [255, 215, 0, 0.2], // 80% transparent gold/yellow border
          width: 4
        }
      });

      const graphic = new Graphic({
        geometry: box,
        symbol: symbol,
        attributes: { isHighlight: true }
      });

      console.log("[SimpleLabelDragger] Created highlight graphic successfully");
      return graphic;
    } catch (error) {
      console.error("[SimpleLabelDragger] Error creating highlight box:", error);
      return null;
    }
  }

  /**
   * Find the data point graphic associated with a label
   * @private
   * @param {Object} labelGraphic - The label graphic
   * @returns {Object|null} - Data point graphic or null
   */
  _findDataPointForLabel(labelGraphic) {
    if (!labelGraphic || !this.view?.map?.layers) return null;

    const parentID = labelGraphic.attributes?.parentID;
    const dataPointGeometry = labelGraphic.attributes?.dataPointGeometry;
    const labelLayerId = labelGraphic.attributes?.layerId;

    console.log("[SimpleLabelDragger] Finding data point for label with parentID:", parentID, "dataPointGeometry:", dataPointGeometry);

    // Search all graphics layers
    for (const layer of this.view.map.layers) {
      if (!layer.graphics) continue;

      for (const graphic of layer.graphics) {
        // Skip labels
        if (graphic.attributes?.isLabel || graphic.symbol?.type === "text") {
          continue;
        }

        // Match by parentID -> OBJECTID (labels have parentID that matches data point's OBJECTID)
        if (parentID !== undefined && parentID !== null) {
          const graphicOBJECTID = graphic.attributes?.OBJECTID;
          if (graphicOBJECTID !== undefined && graphicOBJECTID === parentID) {
            console.log("[SimpleLabelDragger] Found data point by parentID/OBJECTID match:", parentID);
            return graphic;
          }
        }

        // Match by geometry (if dataPointGeometry is stored)
        if (dataPointGeometry && graphic.geometry) {
          // Use x/y or latitude/longitude depending on what's available
          const dpLat = dataPointGeometry.latitude ?? dataPointGeometry.y;
          const dpLon = dataPointGeometry.longitude ?? dataPointGeometry.x;
          const gLat = graphic.geometry.latitude ?? graphic.geometry.y;
          const gLon = graphic.geometry.longitude ?? graphic.geometry.x;

          if (dpLat !== undefined && dpLon !== undefined &&
              gLat !== undefined && gLon !== undefined) {
            const dist = this._getDistance(dpLat, dpLon, gLat, gLon);
            if (dist < 0.0001) { // Very close match
              console.log("[SimpleLabelDragger] Found data point by geometry match");
              return graphic;
            }
          }
        }
      }
    }

    console.log("[SimpleLabelDragger] No data point found for label");
    return null;
  }

  /**
   * Calculate distance between two lat/long points
   * @private
   */
  _getDistance(lat1, lon1, lat2, lon2) {
    const dLat = lat2 - lat1;
    const dLon = lon2 - lon1;
    return Math.sqrt(dLat * dLat + dLon * dLon);
  }

  /**
   * Add highlight boxes for label and its data point
   * @private
   * @param {Object} labelGraphic - The label graphic
   */
  async _addHighlights(labelGraphic) {
    console.log("[SimpleLabelDragger] _addHighlights called");

    if (!this.highlightLayer) {
      console.warn("[SimpleLabelDragger] No highlight layer available, attempting to initialize...");
      await this._initializeHighlightLayer();
      if (!this.highlightLayer) {
        console.error("[SimpleLabelDragger] Failed to initialize highlight layer");
        return;
      }
    }

    if (!labelGraphic) {
      console.error("[SimpleLabelDragger] No label graphic provided");
      return;
    }

    // Remove existing highlights
    this._removeHighlights();

    // Ensure highlight layer is on top
    try {
      const currentIndex = this.view.map.layers.indexOf(this.highlightLayer);
      const topIndex = this.view.map.layers.length - 1;
      if (currentIndex !== topIndex && currentIndex !== -1) {
        this.view.map.reorder(this.highlightLayer, topIndex);
        console.log("[SimpleLabelDragger] Reordered highlight layer to top", {
          from: currentIndex,
          to: topIndex
        });
      }
    } catch (error) {
      console.warn("[SimpleLabelDragger] Could not reorder layer:", error);
    }

    try {
      // Only highlight the data point, not the label itself
      console.log("[SimpleLabelDragger] Finding data point for label");
      const dataPoint = this._findDataPointForLabel(labelGraphic);
      if (dataPoint) {
        console.log("[SimpleLabelDragger] Found data point, creating highlight");
        const markerSize = labelGraphic.attributes?.markerSize || 12;
        const padding = markerSize / 2 + 8;

        this.dataPointHighlight = await this._createHighlightBox(
          dataPoint.geometry,
          null, // Not a text label
          padding
        );

        if (this.dataPointHighlight) {
          this.highlightLayer.add(this.dataPointHighlight);
          console.log("[SimpleLabelDragger] Added data point highlight box", {
            graphicsCount: this.highlightLayer.graphics.length,
            layerVisible: this.highlightLayer.visible
          });
        } else {
          console.warn("[SimpleLabelDragger] Failed to create data point highlight box");
        }
      } else {
        console.warn("[SimpleLabelDragger] No data point found for label");
      }
    } catch (error) {
      console.error("[SimpleLabelDragger] Error adding highlights:", error);
    }
  }

  /**
   * Remove highlight boxes
   * @private
   */
  _removeHighlights() {
    try {
      if (this.highlightLayer) {
        // Remove individual highlights
        if (this.labelHighlight) {
          this.highlightLayer.remove(this.labelHighlight);
          this.labelHighlight = null;
        }

        if (this.dataPointHighlight) {
          this.highlightLayer.remove(this.dataPointHighlight);
          this.dataPointHighlight = null;
        }

        // Remove hover highlight too
        if (this.hoverHighlight) {
          this.highlightLayer.remove(this.hoverHighlight);
          this.hoverHighlight = null;
        }
        this.hoveredLabel = null;

        // Extra safety: remove all graphics from highlight layer to ensure cleanup
        if (this.highlightLayer.graphics && this.highlightLayer.graphics.length > 0) {
          console.log(`[SimpleLabelDragger] Removing ${this.highlightLayer.graphics.length} remaining highlight graphics`);
          this.highlightLayer.removeAll();
        }
      }
    } catch (error) {
      console.warn("[SimpleLabelDragger] Error removing highlights:", error);
      // Try to force cleanup even if there's an error
      this.labelHighlight = null;
      this.dataPointHighlight = null;
      this.hoverHighlight = null;
      this.hoveredLabel = null;
    }
  }

  /**
   * Update highlight boxes during dragging
   * Note: Only the data point is highlighted, and it stays in place (doesn't move with the label)
   * @param {Object} labelGraphic - Optional label graphic (unused, kept for API compatibility)
   * @private
   */
  async _updateHighlightPositions(labelGraphic = null) {
    // Data point highlight stays in place (doesn't move with the label)
    // No updates needed since we only highlight the data point
  }
  
  /**
   * Find a label at the given screen point
   * @param {Object} screenPoint - The screen point to test
   * @returns {Promise<Object|null>} - The label graphic or null if none found
   * @private
   */
  async _findLabelAtPoint(screenPoint) {
    if (!this.view || !this.view.hitTest) {
      return null;
    }
    
    try {
      const response = await this.view.hitTest(screenPoint);
      
      // Validate response
      if (!response || !response.results || !Array.isArray(response.results)) {
        return null;
      }
      
      // Find label hit
      const labelHit = response.results.find(result => 
        result && result.graphic && (
          (result.graphic.symbol && result.graphic.symbol.type === "text") || 
          (result.graphic.attributes && result.graphic.attributes.isLabel === true)
        )
      );
      
      return labelHit ? labelHit.graphic : null;
    } catch (error) {
      console.error("[SimpleLabelDragger] Error finding label:", error);
      return null;
    }
  }

  /**
   * Start direct drag mode for a selected label (click-and-hold drag)
   * @param {Object} screenPoint - The screen point where drag started
   * @private
   */
  async _startDirectDrag(screenPoint) {
    if (!this.selectedLabel || !this.selectedLabel.geometry) {
      console.warn("[SimpleLabelDragger] Cannot start direct drag - no selected label");
      return;
    }

    // Clear hover highlight when starting drag
    this._removeHoverHighlight();

    console.log("[SimpleLabelDragger] Starting direct drag mode");

    // Pre-load and cache the Point class for smooth dragging (avoid async import during drag)
    if (!this._PointClass) {
      try {
        this._PointClass = (await import("@arcgis/core/geometry/Point.js")).default;
        console.log("[SimpleLabelDragger] Point class cached for drag operations");
      } catch (error) {
        console.warn("[SimpleLabelDragger] Could not cache Point class:", error);
      }
    }

    this.isDirectDragging = true;
    this.directDragStartPoint = { x: screenPoint.x, y: screenPoint.y };
    console.log("[SimpleLabelDragger] Set directDragStartPoint:", this.directDragStartPoint);

    // Store original geometry for reference (for cancel/restore)
    this.directDragOriginalGeometry = {
      x: this.selectedLabel.geometry.x,
      y: this.selectedLabel.geometry.y,
      latitude: this.selectedLabel.geometry.latitude,
      longitude: this.selectedLabel.geometry.longitude
    };

    // CRITICAL: Disable map navigation to prevent panning while dragging
    if (this.view.navigation && !this.directDragNavState) {
      this.directDragNavState = {
        browserTouchPanEnabled: this.view.navigation.browserTouchPanEnabled,
        mouseWheelZoomEnabled: this.view.navigation.mouseWheelZoomEnabled,
        keyboardNavigation: this.view.navigation.keyboardNavigation,
        momentumEnabled: this.view.navigation.momentumEnabled,
        dragEnabled: this.view.navigation.dragEnabled
      };

      // Disable all navigation
      this.view.navigation.browserTouchPanEnabled = false;
      this.view.navigation.mouseWheelZoomEnabled = false;
      this.view.navigation.keyboardNavigation = false;
      if (typeof this.view.navigation.momentumEnabled !== 'undefined') {
        this.view.navigation.momentumEnabled = false;
      }
      if (typeof this.view.navigation.dragEnabled !== 'undefined') {
        this.view.navigation.dragEnabled = false;
      }

      console.log("[SimpleLabelDragger] Disabled map navigation for direct drag");
    }

    // Set editing flag on view to prevent other handlers from interfering
    this.view._labelEditingActive = true;

    // Change cursor to indicate dragging
    if (this.view.container) {
      this.view.container.style.cursor = 'grabbing';
    }

    // Call onDragStart callback
    if (this.onDragStart && typeof this.onDragStart === 'function') {
      try {
        this.onDragStart(this.selectedLabel);
      } catch (error) {
        console.error('[SimpleLabelDragger] Error in onDragStart callback:', error);
      }
    }
  }

  /**
   * Handle direct drag movement - update label position to cursor location
   * @param {Object} screenPoint - Current screen point
   * @private
   */
  _handleDirectDragMove(screenPoint) {
    if (!this.isDirectDragging || !this.selectedLabel) {
      return;
    }

    // Log incoming screen point and start point for debugging delta jumps
    const dx = screenPoint.x - this.directDragStartPoint.x;
    const dy = screenPoint.y - this.directDragStartPoint.y;

    // Detect unusual delta jumps (more than 100px difference from expected)
    if (Math.abs(dy) > 150 || Math.abs(dx) > 150) {
      console.warn("[SimpleLabelDragger] LARGE DELTA DETECTED!", {
        screenPoint: { x: screenPoint.x, y: screenPoint.y },
        startPoint: this.directDragStartPoint,
        delta: { dx, dy }
      });
    }

    try {
      // Convert current screen point to map coordinates
      const mapPoint = this.view.toMap(screenPoint);

      if (!mapPoint) {
        console.warn("[SimpleLabelDragger] Could not convert screen point to map point");
        return;
      }

      // Use cached Point class if available, otherwise create geometry from mapPoint directly
      if (this._PointClass) {
        // Create new geometry at the cursor location using cached Point class
        const newGeometry = new this._PointClass({
          x: mapPoint.x,
          y: mapPoint.y,
          spatialReference: this.view.spatialReference
        });
        this.selectedLabel.geometry = newGeometry;
      } else {
        // Fallback: Clone and modify the existing geometry or use mapPoint directly
        // This works because toMap already returns a Point
        this.selectedLabel.geometry = mapPoint;
      }

      // Force refresh the layer containing this label
      // First try the graphic's own layer property
      if (this.selectedLabel.layer && typeof this.selectedLabel.layer.refresh === 'function') {
        this.selectedLabel.layer.refresh();
      }
      // Then try the labelManager's labelLayer (where labels are stored in GraphicsLayer)
      else if (this.labelManager && this.labelManager.labelLayer && typeof this.labelManager.labelLayer.refresh === 'function') {
        this.labelManager.labelLayer.refresh();
      }
      // Finally try the view's graphics collection
      else if (this.view && this.view.graphics && typeof this.view.graphics.refresh === 'function') {
        this.view.graphics.refresh();
      }

      // Call onDrag callback
      const dx = screenPoint.x - this.directDragStartPoint.x;
      const dy = screenPoint.y - this.directDragStartPoint.y;
      if (this.onDrag && typeof this.onDrag === 'function') {
        try {
          this.onDrag(this.selectedLabel, dx, dy);
        } catch (error) {
          console.error('[SimpleLabelDragger] Error in onDrag callback:', error);
        }
      }
    } catch (error) {
      console.error("[SimpleLabelDragger] Error during direct drag move:", error);
    }
  }

  /**
   * End direct drag mode and save the label position
   * @private
   */
  async _endDirectDrag() {
    if (!this.isDirectDragging) {
      return;
    }

    console.log("[SimpleLabelDragger] Ending direct drag mode");

    try {
      // Save the final position to LabelManager
      if (this.labelManager && this.selectedLabel) {
        // Update the label position in the manager
        if (typeof this.labelManager.updateLabelPosition === 'function') {
          const finalPosition = {
            x: 0, // COORDINATE-LOCKED: Always 0
            y: 0  // COORDINATE-LOCKED: Always 0
          };
          this.labelManager.updateLabelPosition(this.selectedLabel, finalPosition);
        }

        // Store new coordinates in editedLabels
        if (this.labelManager.editedLabels && this.selectedLabel.geometry) {
          const labelId = this.labelManager.getLabelId(this.selectedLabel);
          if (labelId) {
            const existingData = this.labelManager.editedLabels.get(labelId) || {};

            // CRITICAL FIX: Convert Web Mercator coordinates to geographic (lat/lon)
            // The geometry may be in Web Mercator (x/y in meters) but we need to save as WGS84 (lat/lon in degrees)
            let latitude, longitude;
            const geom = this.selectedLabel.geometry;

            // Check if the geometry already has latitude/longitude (WGS84)
            if (geom.latitude !== undefined && geom.longitude !== undefined) {
              latitude = geom.latitude;
              longitude = geom.longitude;
            } else if (geom.spatialReference &&
                       (geom.spatialReference.wkid === 3857 || geom.spatialReference.wkid === 102100)) {
              // Web Mercator - need to convert to geographic
              // Import webMercatorUtils and convert
              try {
                const webMercatorUtils = await import("@arcgis/core/geometry/support/webMercatorUtils.js");
                const geoPoint = webMercatorUtils.webMercatorToGeographic(geom);
                if (geoPoint) {
                  latitude = geoPoint.y;
                  longitude = geoPoint.x;
                  console.log("[SimpleLabelDragger] Converted Web Mercator to geographic:", { latitude, longitude });
                }
              } catch (conversionError) {
                console.error("[SimpleLabelDragger] Error converting coordinates:", conversionError);
                // Fallback - this may cause issues but at least try
                latitude = geom.y;
                longitude = geom.x;
              }
            } else {
              // Assume it's already geographic or use raw values
              latitude = geom.y;
              longitude = geom.x;
            }

            this.labelManager.editedLabels.set(labelId, {
              ...existingData,
              graphic: this.selectedLabel,
              latitude: latitude,
              longitude: longitude,
              manuallyPositioned: true
            });
          }
        }

        // Mark as manually positioned
        if (typeof this.labelManager.markLabelAsManuallyPositioned === 'function') {
          this.labelManager.markLabelAsManuallyPositioned(this.selectedLabel);
        }

        // Auto-save positions
        if (typeof this.labelManager.savePositions === 'function') {
          const result = this.labelManager.savePositions(true);
          if (result && typeof result.catch === 'function') {
            result.catch(err => {
              console.error("[SimpleLabelDragger] Error saving after direct drag:", err);
            });
          }
        }
      }

      // Call onDragEnd callback
      if (this.onDragEnd && typeof this.onDragEnd === 'function') {
        try {
          this.onDragEnd(this.selectedLabel);
        } catch (error) {
          console.error('[SimpleLabelDragger] Error in onDragEnd callback:', error);
        }
      }
    } catch (error) {
      console.error("[SimpleLabelDragger] Error ending direct drag:", error);
    } finally {
      // CRITICAL: Restore map navigation
      if (this.view.navigation && this.directDragNavState) {
        this.view.navigation.browserTouchPanEnabled = this.directDragNavState.browserTouchPanEnabled;
        this.view.navigation.mouseWheelZoomEnabled = this.directDragNavState.mouseWheelZoomEnabled;
        if (this.directDragNavState.keyboardNavigation !== undefined) {
          this.view.navigation.keyboardNavigation = this.directDragNavState.keyboardNavigation;
        }
        if (typeof this.view.navigation.momentumEnabled !== 'undefined' &&
            this.directDragNavState.momentumEnabled !== undefined) {
          this.view.navigation.momentumEnabled = this.directDragNavState.momentumEnabled;
        }
        if (typeof this.view.navigation.dragEnabled !== 'undefined' &&
            this.directDragNavState.dragEnabled !== undefined) {
          this.view.navigation.dragEnabled = this.directDragNavState.dragEnabled;
        }
        this.directDragNavState = null;
        console.log("[SimpleLabelDragger] Restored map navigation after direct drag");
      }

      // Reset direct drag state
      this.isDirectDragging = false;
      this.directDragStartPoint = null;
      this.directDragOriginalGeometry = null;

      // Restore cursor
      if (this.view.container) {
        this.view.container.style.cursor = this.isActive ? 'move' : 'default';
      }
    }
  }

  /**
   * Cancel direct drag and restore original position
   * @private
   */
  _cancelDirectDrag() {
    if (!this.isDirectDragging || !this.selectedLabel || !this.directDragOriginalGeometry) {
      return;
    }

    console.log("[SimpleLabelDragger] Canceling direct drag");

    try {
      // Restore original geometry
      import("@arcgis/core/geometry/Point.js").then(module => {
        const Point = module.default;
        const originalGeometry = new Point({
          x: this.directDragOriginalGeometry.x,
          y: this.directDragOriginalGeometry.y,
          spatialReference: this.view.spatialReference
        });

        this.selectedLabel.geometry = originalGeometry;

        // Force refresh
        if (this.view.graphics && typeof this.view.graphics.refresh === 'function') {
          this.view.graphics.refresh();
        }

        // Update highlight positions
        this._updateHighlightPositions();
      });
    } catch (error) {
      console.error("[SimpleLabelDragger] Error canceling direct drag:", error);
    } finally {
      // CRITICAL: Restore map navigation
      if (this.view.navigation && this.directDragNavState) {
        this.view.navigation.browserTouchPanEnabled = this.directDragNavState.browserTouchPanEnabled;
        this.view.navigation.mouseWheelZoomEnabled = this.directDragNavState.mouseWheelZoomEnabled;
        if (this.directDragNavState.keyboardNavigation !== undefined) {
          this.view.navigation.keyboardNavigation = this.directDragNavState.keyboardNavigation;
        }
        if (typeof this.view.navigation.momentumEnabled !== 'undefined' &&
            this.directDragNavState.momentumEnabled !== undefined) {
          this.view.navigation.momentumEnabled = this.directDragNavState.momentumEnabled;
        }
        if (typeof this.view.navigation.dragEnabled !== 'undefined' &&
            this.directDragNavState.dragEnabled !== undefined) {
          this.view.navigation.dragEnabled = this.directDragNavState.dragEnabled;
        }
        this.directDragNavState = null;
        console.log("[SimpleLabelDragger] Restored map navigation after cancel");
      }

      // Reset state
      this.isDirectDragging = false;
      this.directDragStartPoint = null;
      this.directDragOriginalGeometry = null;

      if (this.view.container) {
        this.view.container.style.cursor = this.isActive ? 'move' : 'default';
      }
    }
  }

  /**
   * Handle pointer down for direct drag initiation
   * Right-click dragging is ONLY allowed when the Label Editor is open
   * @param {Object} event - The pointer down event
   * @private
   */
  async _handlePointerDown(event) {
    // Skip if in the middle of other operations
    if (this.isMoving || this.isSelectionPending || this.isDirectDragging) {
      console.log("[SimpleLabelDragger] Skipping pointer-down: already in operation", {
        isMoving: this.isMoving,
        isSelectionPending: this.isSelectionPending,
        isDirectDragging: this.isDirectDragging
      });
      return;
    }

    // CRITICAL: Prevent duplicate event processing during async operations
    // Set this immediately before any async calls
    if (this._pointerDownInProgress) {
      console.log("[SimpleLabelDragger] Skipping duplicate pointer-down event");
      return;
    }
    this._pointerDownInProgress = true;

    // CRITICAL: Only allow right-click (button 2) dragging when Label Editor is open
    // Get the button from native event or event object
    const nativeEvent = event.native || event;
    const button = nativeEvent.button !== undefined ? nativeEvent.button : event.button;

    // Right-click is button 2
    const isRightClick = button === 2;

    // If Label Editor is NOT open, ignore all label drag attempts
    if (!this.labelEditorOpen) {
      // Allow normal map interactions
      this._pointerDownInProgress = false;
      return;
    }

    // If Label Editor IS open, only respond to right-click
    if (!isRightClick) {
      // Left-click can still be used for selection, but not dragging
      this._pointerDownInProgress = false;
      return;
    }

    const screenPoint = this._createValidScreenPoint(event);
    if (!screenPoint) {
      this._pointerDownInProgress = false;
      return;
    }

    try {
      // Check if the pointer is on any label
      const labelAtPoint = await this._findLabelAtPoint(screenPoint);

      if (labelAtPoint) {
      // Check if this is a different label than currently selected
      const currentLabelId = this.selectedLabel ? this.labelManager?.getLabelId(this.selectedLabel) : null;
      const clickedLabelId = this.labelManager?.getLabelId(labelAtPoint);
      const isDifferentLabel = currentLabelId !== clickedLabelId;

      // If clicking on a different label, save the current one first
      if (isDifferentLabel && this.selectedLabel) {
        console.log("[SimpleLabelDragger] Switching to different label - saving current label first");

        // Save current label position
        if (this.labelManager) {
          if (typeof this.labelManager.updateLabelPosition === 'function') {
            const finalPosition = { x: 0, y: 0 };
            this.labelManager.updateLabelPosition(this.selectedLabel, finalPosition);
          }

          // Store coordinates in editedLabels
          if (this.labelManager.editedLabels && this.selectedLabel.geometry) {
            const labelId = this.labelManager.getLabelId(this.selectedLabel);
            if (labelId) {
              const existingData = this.labelManager.editedLabels.get(labelId) || {};
              this.labelManager.editedLabels.set(labelId, {
                ...existingData,
                graphic: this.selectedLabel,
                latitude: this.selectedLabel.geometry.latitude || this.selectedLabel.geometry.y,
                longitude: this.selectedLabel.geometry.longitude || this.selectedLabel.geometry.x,
                manuallyPositioned: true
              });
            }
          }

          // Auto-save positions
          if (typeof this.labelManager.savePositions === 'function') {
            this.labelManager.savePositions(true);
          }
        }

        // Remove highlights from previous label
        this._removeHighlights();
      }

      // Select the new label (or re-select the same one)
      this.selectedLabel = labelAtPoint;

      // Update labelManager's selected label
      if (this.labelManager) {
        this.labelManager.selectedLabel = labelAtPoint;
        if (typeof this.labelManager.selectLabel === 'function') {
          this.labelManager.selectLabel(labelAtPoint);
        }
      }

      // Dispatch selection event for LabelEditor to pick up
      if (typeof window !== 'undefined' && typeof CustomEvent === 'function') {
        const selectionEvent = new CustomEvent('labelSelected', {
          detail: { label: labelAtPoint }
        });
        window.dispatchEvent(selectionEvent);
      }

      console.log("[SimpleLabelDragger] Pointer down on label - selecting and starting direct drag:", clickedLabelId);

      // Add yellow highlight box around the data point to show which point this label belongs to
      await this._addHighlights(labelAtPoint);

      // Start direct drag immediately
      await this._startDirectDrag(screenPoint);

      // Prevent default behavior
      event.stopPropagation();
      if (typeof event.preventDefault === 'function') {
        event.preventDefault();
      }
      }
    } catch (error) {
      console.error("[SimpleLabelDragger] Error in pointer-down handler:", error);
    } finally {
      // ALWAYS reset the in-progress flag, whether success or failure
      this._pointerDownInProgress = false;
    }
  }

  /**
   * Handle pointer move for direct drag and hover detection
   * @param {Object} event - The pointer move event
   * @private
   */
  _handlePointerMove(event) {
    const screenPoint = this._createValidScreenPoint(event);
    if (!screenPoint) {
      return;
    }

    // If actively dragging, handle drag movement
    if (this.isDirectDragging) {
      // Throttle move events
      const now = Date.now();
      if (now - this.lastMoveTime < this.moveThrottle) {
        return;
      }
      this.lastMoveTime = now;

      this._handleDirectDragMove(screenPoint);

      // Prevent default behavior
      event.stopPropagation();
      if (typeof event.preventDefault === 'function') {
        event.preventDefault();
      }
      return;
    }

    // If not dragging and label editor is open, handle hover detection
    if (this.labelEditorOpen && !this.isMoving && !this.isSelectionPending) {
      this._handleHoverDetection(screenPoint);
    }
  }

  /**
   * Handle hover detection for labels
   * @param {Object} screenPoint - The screen point
   * @private
   */
  async _handleHoverDetection(screenPoint) {
    // Skip if dragging started
    if (this.isDirectDragging || this.isMoving) {
      return;
    }

    // Throttle hover detection (30ms)
    const now = Date.now();
    if (now - this.lastHoverTime < 30) {
      return;
    }
    this.lastHoverTime = now;

    try {
      // Use expanded hit test to find labels
      const labelHit = await this._expandedLabelHitTest(screenPoint, 15);

      // Check again after async operation - drag may have started
      if (this.isDirectDragging || this.isMoving) {
        return;
      }

      if (labelHit) {
        // Check if it's a different label than currently hovered
        if (this.hoveredLabel !== labelHit) {
          await this._updateHoverHighlight(labelHit);
          if (this.view?.container && !this.isDirectDragging) {
            this.view.container.style.cursor = "pointer";
          }
        }
      } else {
        // No label under cursor, remove hover highlight
        if (this.hoveredLabel) {
          this._removeHoverHighlight();
          if (this.view?.container && !this.isDirectDragging) {
            this.view.container.style.cursor = "default";
          }
        }
      }
    } catch (error) {
      // Silently ignore hover detection errors
    }
  }

  /**
   * Expanded hit test that checks multiple points around the mouse for better label detection
   * @param {Object} screenPoint - The center screen point
   * @param {number} radius - The radius to check around the point
   * @returns {Promise<Object|null>} - The label graphic or null
   * @private
   */
  async _expandedLabelHitTest(screenPoint, radius = 12) {
    const offsets = [
      { x: 0, y: 0 },           // Center
      { x: -radius, y: 0 },     // Left
      { x: radius, y: 0 },      // Right
      { x: 0, y: -radius },     // Top
      { x: 0, y: radius },      // Bottom
      { x: -radius, y: -radius }, // Top-left
      { x: radius, y: -radius },  // Top-right
      { x: -radius, y: radius },  // Bottom-left
      { x: radius, y: radius },   // Bottom-right
    ];

    for (const offset of offsets) {
      const testPoint = {
        x: screenPoint.x + offset.x,
        y: screenPoint.y + offset.y
      };

      const labelHit = await this._findLabelAtPoint(testPoint);
      if (labelHit) {
        return labelHit;
      }
    }

    return null;
  }

  /**
   * Update the hover highlight for a label
   * @param {Object} labelGraphic - The label graphic to highlight
   * @private
   */
  async _updateHoverHighlight(labelGraphic) {
    // Skip if dragging - don't update hover during drag
    if (this.isDirectDragging || this.isMoving) {
      return;
    }

    // Remove existing hover highlight
    this._removeHoverHighlight();

    if (!labelGraphic || !labelGraphic.geometry || !labelGraphic.symbol) {
      return;
    }

    this.hoveredLabel = labelGraphic;

    // Ensure highlight layer exists
    if (!this.highlightLayer) {
      await this._initializeHighlightLayer();
      if (!this.highlightLayer) {
        return;
      }
    }

    // Check again after async - drag may have started
    if (this.isDirectDragging || this.isMoving) {
      this._removeHoverHighlight();
      return;
    }

    try {
      // Calculate label bounds
      const symbol = labelGraphic.symbol;
      const text = symbol.text || '';
      const fontSize = symbol.font?.size || 10;
      const xoffset = symbol.xoffset || 0;
      const yoffset = symbol.yoffset || 0;
      const horizontalAlignment = symbol.horizontalAlignment || 'center';
      const verticalAlignment = symbol.verticalAlignment || 'middle';

      // Estimate text dimensions
      const estimatedWidth = Math.max(text.length * fontSize * 0.55, 30);
      const estimatedHeight = fontSize * 1.5;
      const padding = 6;

      // Convert label geometry to screen coordinates
      const screenPoint = this.view.toScreen(labelGraphic.geometry);
      if (!screenPoint) return;

      // Apply offsets to get actual label position
      const labelScreenX = screenPoint.x + xoffset;
      const labelScreenY = screenPoint.y - yoffset;

      // Calculate box corners based on text alignment
      let boxLeft, boxRight, boxTop, boxBottom;

      // Handle horizontal alignment
      switch (horizontalAlignment) {
        case 'left':
          // Anchor is at left edge, text extends to the right
          boxLeft = labelScreenX - padding;
          boxRight = labelScreenX + estimatedWidth + padding;
          break;
        case 'right':
          // Anchor is at right edge, text extends to the left
          boxLeft = labelScreenX - estimatedWidth - padding;
          boxRight = labelScreenX + padding;
          break;
        case 'center':
        default:
          // Anchor is at center, text extends equally both ways
          const halfWidth = estimatedWidth / 2 + padding;
          boxLeft = labelScreenX - halfWidth;
          boxRight = labelScreenX + halfWidth;
          break;
      }

      // Handle vertical alignment
      switch (verticalAlignment) {
        case 'top':
          // Anchor is at top, text extends downward
          boxTop = labelScreenY - padding;
          boxBottom = labelScreenY + estimatedHeight + padding;
          break;
        case 'bottom':
          // Anchor is at bottom, text extends upward
          boxTop = labelScreenY - estimatedHeight - padding;
          boxBottom = labelScreenY + padding;
          break;
        case 'middle':
        case 'baseline':
        default:
          // Anchor is at middle/baseline, text extends both ways
          const halfHeight = estimatedHeight / 2 + padding;
          boxTop = labelScreenY - halfHeight;
          boxBottom = labelScreenY + halfHeight;
          break;
      }

      // Convert corners back to map coordinates
      const topLeft = this.view.toMap({ x: boxLeft, y: boxTop });
      const topRight = this.view.toMap({ x: boxRight, y: boxTop });
      const bottomRight = this.view.toMap({ x: boxRight, y: boxBottom });
      const bottomLeft = this.view.toMap({ x: boxLeft, y: boxBottom });

      if (!topLeft || !topRight || !bottomRight || !bottomLeft) return;

      // Import Graphic class
      const [{ default: Graphic }] = await Promise.all([
        import("@arcgis/core/Graphic")
      ]);

      // Final check - bail out if drag started during async import
      if (this.isDirectDragging || this.isMoving) {
        return;
      }

      // Create yellow hover highlight graphic
      this.hoverHighlight = new Graphic({
        geometry: {
          type: "polygon",
          rings: [[
            [topLeft.x, topLeft.y],
            [topRight.x, topRight.y],
            [bottomRight.x, bottomRight.y],
            [bottomLeft.x, bottomLeft.y],
            [topLeft.x, topLeft.y]
          ]],
          spatialReference: this.view.spatialReference
        },
        symbol: {
          type: "simple-fill",
          color: [255, 235, 59, 0.4], // Bright yellow with transparency
          outline: {
            color: [255, 193, 7, 1], // Amber/gold border
            width: 2
          }
        },
        attributes: {
          isHoverHighlight: true
        }
      });

      // Add to highlight layer
      this.highlightLayer.add(this.hoverHighlight);
      console.log("[SimpleLabelDragger] Added hover highlight for:", text);
    } catch (error) {
      console.error("[SimpleLabelDragger] Error creating hover highlight:", error);
    }
  }

  /**
   * Remove the hover highlight
   * @private
   */
  _removeHoverHighlight() {
    if (this.hoverHighlight && this.highlightLayer) {
      try {
        this.highlightLayer.remove(this.hoverHighlight);
      } catch (error) {
        // Silently ignore
      }
      this.hoverHighlight = null;
    }
    this.hoveredLabel = null;
  }

  /**
   * Handle pointer up for direct drag end
   * @param {Object} event - The pointer up event
   * @private
   */
  _handlePointerUp(event) {
    // Clear any pending hold timer
    if (this.directDragHoldTimer) {
      clearTimeout(this.directDragHoldTimer);
      this.directDragHoldTimer = null;
    }

    if (this.isDirectDragging) {
      this._endDirectDrag();

      // Prevent default behavior
      event.stopPropagation();
      if (typeof event.preventDefault === 'function') {
        event.preventDefault();
      }
    }
  }

  /**
   * Handle map click events - either select a label or drop the currently selected one
   * @param {Object} event - The click event
   * @private
   */
  async _handleMapClick(event) {
    try {
      // Ensure we don't have any pending selection operations
      if (this.isSelectionPending) {
        return;
      }
      this.isSelectionPending = true;
      
      const screenPoint = this._createValidScreenPoint(event);
      if (!screenPoint) {
        this.isSelectionPending = false;
        return;
      }
      
      // Prevent default event behavior
      event.stopPropagation();
      if (typeof event.preventDefault === 'function') {
        event.preventDefault();
      }
      
      // If already moving a label, this click is to drop it
      if (this.isMoving && this.selectedLabel) {
        this._dropLabel();
        this.isSelectionPending = false;
        return;
      }
      
      // Otherwise, try to find and select a label
      const labelGraphic = await this._findLabelAtPoint(screenPoint);
      
      if (labelGraphic) {
        // First, explicitly update the selectedLabel in the labelManager
        if (this.labelManager && typeof this.labelManager.selectLabel === 'function') {
          // Directly set the labelManager.selectedLabel property if it exists
          if (this.labelManager.hasOwnProperty('selectedLabel')) {
            this.labelManager.selectedLabel = labelGraphic;
          }
          
          // Call the selectLabel method
          this.labelManager.selectLabel(labelGraphic);
        }
        
        // Then handle the selection UI
        this._selectLabel(labelGraphic, screenPoint);
        
        // Update this.selectedLabel
        this.selectedLabel = labelGraphic;
        
        // Log the selection
        console.log(`[SimpleLabelDragger] Selected label (ID: ${this.labelManager.getLabelId(labelGraphic)})`);
        
        // Save selectedLabel to window for debugging and direct access
        if (typeof window !== 'undefined') {
          window._selectedLabel = labelGraphic;
        }
        
        // Force the labelManager to emit a change event if it has such a method
        if (this.labelManager && typeof this.labelManager.notifySelectionChanged === 'function') {
          this.labelManager.notifySelectionChanged(labelGraphic);
        }
        
        // Dispatch a custom event that the React component can listen for
        if (typeof window !== 'undefined' && typeof CustomEvent === 'function') {
          const event = new CustomEvent('labelSelected', {
            detail: { label: labelGraphic }
          });
          window.dispatchEvent(event);
          this.view.container.dispatchEvent(event);
        }
      } else {
        // No label found at click point - deselect any existing selection
        if (this.labelManager) {
          if (typeof this.labelManager.updateLabelPosition === 'function') {
            const finalPosition = {
              x: 0, // COORDINATE-LOCKED: Always 0
              y: 0  // COORDINATE-LOCKED: Always 0
            };
            this.labelManager.updateLabelPosition(this.selectedLabel, finalPosition);
          }
          
          // Auto-save positions
          if (typeof this.labelManager.savePositions === 'function') {
            const result = this.labelManager.savePositions(true);
            // Check if result is a Promise before using .catch
            if (result && typeof result.catch === 'function') {
              result.catch(err => {
                console.error("[LabelDragger] Error saving after drag:", err);
              });
            }
          }
        }
        
        // Clear local selection
        this.selectedLabel = null;
        
        if (typeof window !== 'undefined') {
          window._selectedLabel = null;
        }
        
        // Log the deselection
        console.log('[SimpleLabelDragger] No label found at click point - deselected');
        
        // Force the labelManager to emit a change event if it has such a method
        if (this.labelManager && typeof this.labelManager.notifySelectionChanged === 'function') {
          this.labelManager.notifySelectionChanged(null);
        }
        
        // Dispatch a custom event for deselection
        if (typeof window !== 'undefined' && typeof CustomEvent === 'function') {
          const event = new CustomEvent('labelSelected', {
            detail: { label: null }
          });
          window.dispatchEvent(event);
          this.view.container.dispatchEvent(event);
        }
      }
      
      // Clear selection pending flag
      this.isSelectionPending = false;
    } catch (error) {
      console.error("[SimpleLabelDragger] Error handling map click:", error);
      this.isSelectionPending = false;
    }
  }
  
  /**
   * Select a label and prepare for moving
   * @param {Object} labelGraphic - The label graphic to select
   * @param {Object} screenPoint - The screen point where the click occurred
   * @private
   */
  async _selectLabel(labelGraphic, screenPoint) {
    // If we're in active mode, prepare for moving
    if (this.isActive) {
      // Store the selected label
      this.selectedLabel = labelGraphic;

      // Store original position
      this.originalPosition = screenPoint;
      this.currentPosition = { ...screenPoint };

      // Get initial offset (COORDINATE-LOCKED: Always 0)
      this.originalOffset = {
        x: 0, // COORDINATE-LOCKED: No pixel offsets
        y: 0  // COORDINATE-LOCKED: No pixel offsets
      };

      // Set moving state
      this.isMoving = true;

      // Change cursor
      if (this.view.container) {
        this.view.container.style.cursor = 'grabbing';
      }

      // Add yellow highlight boxes around label and data point
      console.log("[SimpleLabelDragger] Adding highlights for selected label");
      await this._addHighlights(labelGraphic);

      // Call the onDragStart callback if provided
      if (this.onDragStart && typeof this.onDragStart === 'function') {
        try {
          this.onDragStart(labelGraphic);
        } catch (error) {
          console.error('[SimpleLabelDragger] Error in onDragStart callback:', error);
        }
      }
    }
  }
  
  /**
   * Drop the currently selected label
   * @private
   */
  _dropLabel() {
    if (!this.selectedLabel || !this.isMoving) return;

    try {
      // Save the final position to LabelManager if available
      if (this.labelManager) {
        if (typeof this.labelManager.updateLabelPosition === 'function') {
          const finalPosition = {
            x: 0, // COORDINATE-LOCKED: Always 0
            y: 0  // COORDINATE-LOCKED: Always 0
          };
          this.labelManager.updateLabelPosition(this.selectedLabel, finalPosition);
        }

        // Mark the label as manually positioned to prevent auto-repositioning
        if (typeof this.labelManager.markLabelAsManuallyPositioned === 'function') {
          this.labelManager.markLabelAsManuallyPositioned(this.selectedLabel);
        }

        // Auto-save positions
        if (typeof this.labelManager.savePositions === 'function') {
          this.labelManager.savePositions(true);
        }
      }

      // Call the onDragEnd callback if provided
      if (this.onDragEnd && typeof this.onDragEnd === 'function') {
        this.onDragEnd(this.selectedLabel);
      }

      console.log(`[SimpleLabelDragger] Dropped label (ID: ${this.labelManager.getLabelId(this.selectedLabel)})`);
    } catch (error) {
      console.error('[SimpleLabelDragger] Error dropping label:', error);
    } finally {
      // Reset moving state
      this.isMoving = false;

      // Keep the label selected for editing
      // (But clear the position tracking)
      this.originalPosition = null;
      this.currentPosition = null;

      // Keep highlight boxes visible after dropping (don't remove them)
      // They will be removed when the label is deselected or editing mode is exited

      // Restore cursor to move (still in edit mode)
      if (this.view.container) {
        this.view.container.style.cursor = 'move';
      }
    }
  }
  
  /**
   * Handle mouse/pointer movement to update label position
   * @param {Object} event - The move event
   * @private
   */
  _handleMouseMove(event) {
    // Skip if not in moving state
    if (!this.isMoving || !this.selectedLabel) return;
    
    // Throttle move events to prevent browser slowdown
    const now = Date.now();
    if (now - this.lastMoveTime < this.moveThrottle) {
      return;
    }
    this.lastMoveTime = now;
    
    try {
      // Get current mouse position
      const screenPoint = this._createValidScreenPoint(event);
      if (!screenPoint) return;
      
      // Update current position
      this.currentPosition = screenPoint;
      
      // Calculate offset delta
      const dx = screenPoint.x - this.originalPosition.x;
      const dy = screenPoint.y - this.originalPosition.y;
      
      // Update label position
      if (this.selectedLabel && this.selectedLabel.symbol) {
        // Create a new symbol to force redraw (COORDINATE-LOCKED)
        const newSymbol = this.selectedLabel.symbol.clone();
        
        // COORDINATE-LOCKED: No pixel offsets during drag
        newSymbol.xoffset = 0;
        newSymbol.yoffset = 0;
        newSymbol.horizontalAlignment = "left";
        newSymbol.verticalAlignment = "baseline";
        
        // Apply the updated symbol
        this.selectedLabel.symbol = newSymbol;
        
        // Force refresh using multiple strategies
        if (this.view && typeof this.view.redraw === 'function') {
          this.view.redraw();
        }
        if (this.selectedLabel.layer && typeof this.selectedLabel.layer.refresh === 'function') {
          this.selectedLabel.layer.refresh();
        }
        // Also try the labelManager's labelLayer (where labels are stored in GraphicsLayer)
        else if (this.labelManager && this.labelManager.labelLayer && typeof this.labelManager.labelLayer.refresh === 'function') {
          this.labelManager.labelLayer.refresh();
        }
        if (this.view.graphics && typeof this.view.graphics.refresh === 'function') {
          this.view.graphics.refresh();
        }

        // Update highlight box to follow the label
        this._updateHighlightPositions();

        // Call the onDrag callback if provided
        if (this.onDrag && typeof this.onDrag === 'function') {
          this.onDrag(this.selectedLabel, dx, dy);
        }
      }
    } catch (error) {
      console.error('[SimpleLabelDragger] Error updating label position:', error);
      
      // Try direct property update as a fallback
      try {
        if (this.selectedLabel && this.selectedLabel.symbol) {
          // COORDINATE-LOCKED: No pixel offset fallback
          this.selectedLabel.symbol.xoffset = 0;
          this.selectedLabel.symbol.yoffset = 0;
          
          if (this.view.graphics) {
            this.view.graphics.refresh();
          }
        }
      } catch (fallbackError) {
        console.error('[SimpleLabelDragger] Fallback position update failed:', fallbackError);
      }
    }
  }

  /**
   * Activate the dragger
   * @returns {boolean} Whether activation was successful
   */
  activate() {
    if (!this.view || !this.labelManager) {
      console.warn("[SimpleLabelDragger] Cannot activate: Missing view or labelManager");
      return false;
    }
    
    // Skip if already active
    if (this.isActive) {
      console.log("[SimpleLabelDragger] Already active");
      return true;
    }
    
    // Change cursor to indicate selectable mode
    if (this.view.container) {
      this.view.container.style.cursor = 'move';
      this.view.container.classList.add('label-edit-mode');
    }
    
    // Set up event handlers
    try {
      // Click/tap handler for selecting and dropping labels
      const clickHandler = this.view.on("click", event => this._handleMapClick(event));
      this.handlers.push(clickHandler);
      
      // Mouse/pointer move handler for tracking movement
      const moveHandler = this.view.on("pointer-move", event => this._handleMouseMove(event));
      this.handlers.push(moveHandler);
      
      // Add global mouse move handler for more reliable tracking
      const globalMoveHandler = event => {
        if (this.isMoving && this.selectedLabel) {
          this._handleMouseMove(event);
        }
      };
      document.addEventListener('mousemove', globalMoveHandler);
      this.domEventHandlers.push({
        element: document,
        type: 'mousemove',
        handler: globalMoveHandler
      });
      
      // Add escape key handler to cancel moving
      const keyHandler = event => {
        if (event.key === 'Escape' && this.isMoving) {
          this._cancelMove();
        }
      };
      document.addEventListener('keydown', keyHandler);
      this.domEventHandlers.push({
        element: document,
        type: 'keydown',
        handler: keyHandler
      });
      
      // Add pointer leave handler for map container
      const pointerLeaveHandler = event => {
        // Don't cancel move when pointer leaves - we'll use the global handlers
        // Just update the move indicator
        if (this.isMoving && this.moveIndicator) {
          const screenPoint = this._createValidScreenPoint(event);
          if (screenPoint) {
            this.moveIndicator.style.left = `${screenPoint.x}px`;
            this.moveIndicator.style.top = `${screenPoint.y}px`;
          }
        }
      };
      this.view.container.addEventListener('pointerleave', pointerLeaveHandler);
      this.domEventHandlers.push({
        element: this.view.container,
        type: 'pointerleave',
        handler: pointerLeaveHandler
      });
      
      // Add a special handler to intercept existing click events to ensure selection works
      const captureClickHandler = (event) => {
        // Check if the event target is within our map container
        if (this.view.container.contains(event.target)) {
          // Only proceed if we're in position adjustment mode and not already moving
          if (this.isActive && !this.isMoving && !this.isSelectionPending) {
            this._handleMapClick(event);
          }
        }
      };
      
      // Use capture phase to get events before other handlers
      document.addEventListener('click', captureClickHandler, true);
      this.domEventHandlers.push({
        element: document,
        type: 'click',
        handler: captureClickHandler,
        useCapture: true
      });
      
    } catch (error) {
      console.error("[SimpleLabelDragger] Error setting up event handlers:", error);
      this.deactivate();
      return false;
    }
    
    // Disable map navigation to prevent conflicts
    if (this.view.navigation) {
      this.originalNavState = {
        browserTouchPanEnabled: this.view.navigation.browserTouchPanEnabled,
        mouseWheelZoomEnabled: this.view.navigation.mouseWheelZoomEnabled,
        keyboardNavigation: this.view.navigation.keyboardNavigation,
        momentumEnabled: this.view.navigation.momentumEnabled,
        dragEnabled: this.view.navigation.dragEnabled
      };
      
      this.view.navigation.browserTouchPanEnabled = false;
      this.view.navigation.mouseWheelZoomEnabled = false;
      this.view.navigation.keyboardNavigation = false;
      
      // Disable additional navigation properties if they exist
      if (typeof this.view.navigation.momentumEnabled !== 'undefined') {
        this.view.navigation.momentumEnabled = false;
      }
      if (typeof this.view.navigation.dragEnabled !== 'undefined') {
        this.view.navigation.dragEnabled = false;
      }
    }
    
    // Set a flag on the view to indicate editing mode
    this.view._labelEditingActive = true;
    this.isActive = true;
    
    console.log("[SimpleLabelDragger] Activated with click-to-select, move, click-to-drop behavior");
    return true;
  }
  
  /**
   * Cancel the current move operation without saving
   * @private
   */
  _cancelMove() {
    if (!this.isMoving || !this.selectedLabel) return;
    
    try {
      console.log("[SimpleLabelDragger] Canceling move operation");
      
      // Restore coordinate-locked position
      if (this.selectedLabel && this.selectedLabel.symbol && this.originalOffset) {
        const newSymbol = this.selectedLabel.symbol.clone();
        
        // COORDINATE-LOCKED: Always restore to 0 offsets
        newSymbol.xoffset = 0;
        newSymbol.yoffset = 0;
        newSymbol.horizontalAlignment = "left";
        newSymbol.verticalAlignment = "baseline";
        
        this.selectedLabel.symbol = newSymbol;
        
        // Force refresh
        if (this.view.graphics) {
          this.view.graphics.refresh();
        }
      }
    } catch (error) {
      console.error("[SimpleLabelDragger] Error canceling move:", error);
    } finally {
      // Reset moving state
      this.isMoving = false;
      this.originalPosition = null;
      this.currentPosition = null;
      
      // Restore cursor
      if (this.view.container) {
        this.view.container.style.cursor = 'move';
      }
    }
  }
  
  /**
   * Select a label directly (for use from external code)
   * @param {Object} labelGraphic - The label graphic to select
   * @returns {boolean} - Whether the selection was successful
   */
  selectLabel(labelGraphic) {
    if (!labelGraphic) {
      return false;
    }
    
    try {
      // Set the selected label in the manager
      if (this.labelManager) {
        if (this.labelManager.hasOwnProperty('selectedLabel')) {
          this.labelManager.selectedLabel = labelGraphic;
        }
        
        if (typeof this.labelManager.selectLabel === 'function') {
          this.labelManager.selectLabel(labelGraphic);
        }
      }
      
      // Update local state
      this.selectedLabel = labelGraphic;
      
      // Log the selection
      console.log(`[SimpleLabelDragger] Externally selected label (ID: ${this.labelManager.getLabelId(labelGraphic)})`);
      
      // Force notification events if available
      if (this.labelManager && typeof this.labelManager.notifySelectionChanged === 'function') {
        this.labelManager.notifySelectionChanged(labelGraphic);
      }
      
      // Dispatch a custom event
      if (typeof window !== 'undefined' && typeof CustomEvent === 'function') {
        const event = new CustomEvent('labelSelected', {
          detail: { label: labelGraphic }
        });
        window.dispatchEvent(event);
        if (this.view.container) {
          this.view.container.dispatchEvent(event);
        }
      }
      
      return true;
    } catch (error) {
      console.error("[SimpleLabelDragger] Error in selectLabel:", error);
      return false;
    }
  }

  /**
   * Deactivate the dragger
   * @returns {boolean} Whether deactivation was successful
   */
  deactivate() {
    // Skip if not active
    if (!this.isActive) {
      return true;
    }
    
    // Cancel any ongoing move operation
    if (this.isMoving) {
      this._cancelMove();
    }

    // Cancel any ongoing direct drag operation
    if (this.isDirectDragging) {
      this._cancelDirectDrag();
    }

    // Reset cursor
    if (this.view?.container) {
      this.view.container.style.cursor = 'default';
      this.view.container.classList.remove('label-edit-mode');
    }
    
    // Restore map navigation
    if (this.view?.navigation && this.originalNavState) {
      this.view.navigation.browserTouchPanEnabled = this.originalNavState.browserTouchPanEnabled;
      this.view.navigation.mouseWheelZoomEnabled = this.originalNavState.mouseWheelZoomEnabled;
      
      if (this.originalNavState.keyboardNavigation !== undefined) {
        this.view.navigation.keyboardNavigation = this.originalNavState.keyboardNavigation;
      }
      if (typeof this.view.navigation.momentumEnabled !== 'undefined' && 
          this.originalNavState.momentumEnabled !== undefined) {
        this.view.navigation.momentumEnabled = this.originalNavState.momentumEnabled;
      }
      if (typeof this.view.navigation.dragEnabled !== 'undefined' && 
          this.originalNavState.dragEnabled !== undefined) {
        this.view.navigation.dragEnabled = this.originalNavState.dragEnabled;
      }
      
      this.originalNavState = null;
    }
    
    // Remove the editing flag
    if (this.view) {
      this.view._labelEditingActive = false;
    }
    
    // Clean up event handlers
    this.cleanup();

    // Remove move indicator
    this._removeMoveIndicator();

    // Remove highlight boxes
    this._removeHighlights();

    // Remove highlight layer if it exists
    if (this.highlightLayer && this.view?.map) {
      try {
        this.view.map.remove(this.highlightLayer);
      } catch (error) {
        // Silently ignore errors
      }
      this.highlightLayer = null;
    }

    // Reset state
    this.isActive = false;
    this.selectedLabel = null;
    this.isMoving = false;
    this.originalPosition = null;
    this.currentPosition = null;
    this.originalOffset = null;

    // Reset direct drag state
    this.isDirectDragging = false;
    this.directDragStartPoint = null;
    this.directDragOriginalGeometry = null;
    this.directDragNavState = null;
    if (this.directDragHoldTimer) {
      clearTimeout(this.directDragHoldTimer);
      this.directDragHoldTimer = null;
    }

    console.log("[SimpleLabelDragger] Deactivated");
    return true;
  }

  /**
   * Enables or disables the dragger
   * @param {boolean} enable - Whether to enable or disable
   */
  enable(enable = true) {
    return enable ? this.activate() : this.deactivate();
  }

  /**
   * Set whether the Label Editor is open
   * Right-click dragging is ONLY allowed when the Label Editor is open
   * @param {boolean} isOpen - Whether the Label Editor is open
   */
  setLabelEditorOpen(isOpen) {
    this.labelEditorOpen = isOpen;
    console.log(`[SimpleLabelDragger] Label Editor ${isOpen ? 'opened' : 'closed'} - right-click dragging ${isOpen ? 'enabled' : 'disabled'}`);

    // Update cursor based on state
    if (this.view && this.view.container) {
      if (isOpen && this.isActive) {
        this.view.container.style.cursor = 'move';
      } else if (!isOpen) {
        this.view.container.style.cursor = 'default';
      }
    }
  }

  /**
   * Check if the Label Editor is open
   * @returns {boolean} Whether the Label Editor is open
   */
  isLabelEditorOpen() {
    return this.labelEditorOpen;
  }

  /**
   * Clean up event handlers
   */
  cleanup() {
    // Remove ArcGIS event handlers
    this.handlers.forEach(handler => {
      if (handler && typeof handler.remove === 'function') {
        try {
          handler.remove();
        } catch (e) {
          // Ignore removal errors
        }
      }
    });
    this.handlers = [];

    // Remove DOM event handlers
    this.domEventHandlers.forEach(handlerInfo => {
      if (handlerInfo && handlerInfo.element && handlerInfo.type && handlerInfo.handler) {
        try {
          handlerInfo.element.removeEventListener(
            handlerInfo.type,
            handlerInfo.handler,
            handlerInfo.useCapture || false
          );
        } catch (e) {
          // Ignore removal errors
        }
      }
    });
    this.domEventHandlers = [];

    // Remove page unload handler
    if (this._pageUnloadHandler && typeof window !== 'undefined') {
      try {
        window.removeEventListener('beforeunload', this._pageUnloadHandler);
        window.removeEventListener('pagehide', this._pageUnloadHandler);
        this._pageUnloadHandler = null;
      } catch (e) {
        // Ignore removal errors
      }
    }
  }
}

export default SimpleLabelDragger;